import { PATH, RESERVED_NAME } from '../constants'
import { capitalize } from './strings'

/**
 * Set meta tag with name description (for SEO, for example)
 * @param content
 */
export const addMetaDescription = (content = '') => {
  const el = document.querySelector('meta[name="description"]')

  if (el instanceof HTMLMetaElement) {
    el.content = content
    return
  }

  const meta = document.createElement('meta')
  meta.name = 'description'
  meta.content = content
  document.getElementsByTagName('head')?.[0]?.appendChild(meta)
}

// Usually postfix is added to make the 'meta' title unique,
// because often the 'meta' title is the content taken from the page
export const addDocumentTitle = (content = '', withPostfix = true) => {
  const postfix = withPostfix ? ' | UVOtv' : ''
  document.title = content ? `${content}${postfix}` : 'UVOtv'
}

export const addCustomDocumentTitle = (content = '') => {
  document.title = content
}

/**
 * Remove title and description tags
 */
export const removeDocumentTitle = () => {
  const titleEl = document.querySelector('title')
  if (titleEl) {
    titleEl.remove()
  }
}

export const removeMetaDescription = () => {
  const el = document.querySelector('meta[name="description"]')
  if (el) {
    el.remove()
  }
}

/**
 * Tell Googlebot to index or not the page
 * @param isIndexing
 */
export const addMetaRobots = (isIndexing: boolean = true) => {
  let meta = document.querySelector('meta[name="robots"]')

  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute('name', 'robots')
    document.head.appendChild(meta)
  }

  meta.setAttribute('content', isIndexing ? 'all' : 'noindex, nofollow')
}

/**
 * The function is used for SEO purposes.
 * You need to set the entire URL (absolute path) with protocol, domain, etc.
 * At the moment we can use window.location.href, because we have no duplicate pages.
 * If duplicate pages appear, you will need to determine which of them is canonical.
 */
export const addLinkCanonical = () => {
  const href = window.location.href || ''

  let link = document.querySelector('link[rel="canonical"]')

  if (!link) {
    link = document.createElement('link')
    link.setAttribute('rel', 'canonical')
    link.setAttribute('href', href)
    document.head.appendChild(link)
  }

  link.setAttribute('href', href)
}

export const removeLinkCanonical = (): void => {
  const canonicalLink = document.querySelector('link[rel="canonical"]')
  if (canonicalLink) {
    canonicalLink.remove()
  }
}

// Helper function to check if canonical link should be added based on robots.txt rules
// if allow indexing add canonical link
// if disallow indexing do not add/remove canonical link
export const shouldAddCanonical = (pathname: string): boolean => {
  // Check disallowed patterns from robots.txt
  if (pathname.startsWith(`${PATH.LIVE}${PATH.CATEGORIES}/`)) {
    // Allow channel pages in 'all' category
    if (pathname.includes(`/${RESERVED_NAME.ALL}/`)) return true

    // Allow region pages
    if (pathname.split('/').length === 4) return true

    // Allow category pages
    if (pathname.split('/').length === 5) return true

    // Disallow channel pages in specific categories
    if (pathname.split('/').length >= 6) return false
  }

  // For all other pages canonical should be added
  return true
}

export interface ISeoData {
  metaTitle: string
  metaDescription: string
}

/**
 * Generate SEO data for Live TV pages
 * @param region Region name
 * @param category Category name
 * @returns Object with metaTitle and metaDescription
 */
export const getSeoData = (region: string, category: string): ISeoData => {
  const formattedRegion = capitalize(region.replaceAll('-', ' '))
  const formattedCategory = capitalize(category.replaceAll('-', ' '))

  return {
    metaTitle: `${formattedRegion} ${formattedCategory} | Live TV Streaming on UVOtv`,
    metaDescription: `Watch ${formattedCategory} TV channels from ${formattedRegion} online on UVOtv for free`,
  }
}
